/**
 * Connected repositories, and the wizard that onboards a script from one.
 *
 * The wizard reads whatever argparse or click the script already declares and proposes a
 * manifest, so onboarding is a review step rather than retyping an interface that
 * already exists.
 */

import { useState } from "react";
import { Link } from "react-router-dom";

import type {
  BundleDraft,
  BundleUploaded,
  ManifestDraft,
  Repo,
  SyncResult,
  UnregisteredScript,
} from "@/api/endpoints";
import {
  draftBundleManifest,
  endpoints,
  uploadBundle,
  validateBundleManifest,
} from "@/api/endpoints";
import { useProject } from "@/components/ProjectLayout";
import { Badge, Empty, ErrorState, Field, Loading, Notice, RelativeTime } from "@/components/ui";
import { useAsync } from "@/hooks/useAsync";

function ConnectRepo({ onConnected }: { onConnected: () => void }) {
  const { project } = useProject();
  const [open, setOpen] = useState(false);
  const [owner, setOwner] = useState("");
  const [name, setName] = useState("");
  const [installationId, setInstallationId] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();

  if (!open) {
    return (
      <button type="button" className="button button--primary" onClick={() => setOpen(true)}>
        Connect a repository
      </button>
    );
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(undefined);
    try {
      await endpoints.connectRepo(project.slug, {
        owner,
        name,
        installation_id: Number(installationId),
        manifest_dir: ".delivery",
        commit_mode: "direct",
      });
      setOpen(false);
      onConnected();
    } catch (caught) {
      setProblem((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <div className="card__header">
        <span className="card__title">Connect a repository</span>
      </div>
      <div className="card__body stack" style={{ gap: 14 }}>
        {problem && <Notice tone="danger">{problem}</Notice>}
        <Notice tone="info">
          The repository must already have the Delivery Hub GitHub App installed. The
          installation id is in the app's URL on GitHub.
        </Notice>

        <div className="row" style={{ gap: 12, alignItems: "flex-end" }}>
          <div style={{ flex: 1, minWidth: 140 }}>
            <Field label="Owner" required htmlFor="repo-owner">
              <input
                id="repo-owner"
                className="input"
                value={owner}
                required
                placeholder="appen"
                onChange={(event) => setOwner(event.target.value)}
              />
            </Field>
          </div>
          <div style={{ flex: 1, minWidth: 140 }}>
            <Field label="Repository" required htmlFor="repo-name">
              <input
                id="repo-name"
                className="input"
                value={name}
                required
                placeholder="delivery-scripts"
                onChange={(event) => setName(event.target.value)}
              />
            </Field>
          </div>
          <div style={{ width: 160 }}>
            <Field label="Installation id" required htmlFor="repo-install">
              <input
                id="repo-install"
                className="input"
                type="number"
                value={installationId}
                required
                onChange={(event) => setInstallationId(event.target.value)}
              />
            </Field>
          </div>
        </div>

        <div className="row">
          <button type="submit" className="button button--primary" disabled={busy}>
            Connect
          </button>
          <button type="button" className="button button--ghost" onClick={() => setOpen(false)}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
}

function Wizard({
  repo,
  file,
  onClose,
  onCommitted,
}: {
  repo: Repo;
  file: UnregisteredScript;
  onClose: () => void;
  onCommitted: () => void;
}) {
  const { project } = useProject();
  const [scriptName, setScriptName] = useState(
    file.path.split("/").pop()?.replace(/\.py$/, "") ?? "",
  );
  const [draft, setDraft] = useState<ManifestDraft>();
  const [yaml, setYaml] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();
  const [validationError, setValidationError] = useState<string | null>(null);

  const generate = async () => {
    setBusy(true);
    setProblem(undefined);
    try {
      const result = await endpoints.draftManifest(project.slug, repo.id, {
        entrypoint: file.path,
        script_name: scriptName,
      });
      setDraft(result);
      setYaml(result.yaml);
      setValidationError(null);
    } catch (caught) {
      setProblem((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const check = async () => {
    const result = await endpoints.validateManifest(project.slug, repo.id, yaml);
    setValidationError(result.valid ? null : (result.error ?? "Not valid."));
  };

  const commit = async () => {
    setBusy(true);
    setProblem(undefined);
    try {
      await endpoints.commitManifest(project.slug, repo.id, {
        yaml,
        message: `Add delivery manifest for ${scriptName}`,
      });
      onCommitted();
    } catch (caught) {
      setProblem((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const lowConfidence = (draft?.detected ?? []).filter((p) => p.confidence === "low");

  return (
    <div className="card">
      <div className="card__header">
        <span className="card__title">Onboard {file.path}</span>
        <button type="button" className="button button--small button--ghost" onClick={onClose}>
          Close
        </button>
      </div>

      <div className="card__body stack" style={{ gap: 14 }}>
        {problem && <Notice tone="danger">{problem}</Notice>}

        {!draft ? (
          <>
            <Field
              label="Script name"
              hint="How it will appear in this project."
              required
              htmlFor="wiz-name"
            >
              <input
                id="wiz-name"
                className="input"
                value={scriptName}
                onChange={(event) => setScriptName(event.target.value)}
              />
            </Field>
            <div>
              <button
                type="button"
                className="button button--primary"
                disabled={busy || !scriptName}
                onClick={generate}
              >
                {busy ? "Reading the script…" : "Read the script"}
              </button>
            </div>
          </>
        ) : (
          <>
            <Notice tone={draft.framework ? "success" : "warning"}>
              {draft.framework
                ? `Found a ${draft.framework} interface with ${draft.detected.length} parameters.`
                : "No argparse or click interface was found. Add parameters by hand below."}
            </Notice>

            {draft.warnings.length > 0 && (
              <Notice tone="warning" title="Worth checking">
                {draft.warnings.map((warning, index) => (
                  <div key={index}>{warning}</div>
                ))}
              </Notice>
            )}

            {lowConfidence.length > 0 && (
              <Notice tone="warning" title="Check these before committing">
                These could not be read literally, so they were guessed:{" "}
                {lowConfidence.map((p) => p.name).join(", ")}.
              </Notice>
            )}

            {draft.detected.length > 0 && (
              <div className="table__scroll">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <th>Type</th>
                      <th>Flag</th>
                      <th>Required</th>
                    </tr>
                  </thead>
                  <tbody>
                    {draft.detected.map((param) => (
                      <tr key={param.name}>
                        <td className="mono">
                          {param.name}
                          {param.confidence === "low" && (
                            <>
                              {" "}
                              <Badge tone="warning">guessed</Badge>
                            </>
                          )}
                        </td>
                        <td className="muted">{param.type}</td>
                        <td className="mono muted">{param.arg ?? "positional"}</td>
                        <td className="muted">{param.required ? "yes" : "no"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <Field
              label="Manifest"
              hint="Committed to the repository, so it is reviewable in a diff like anything else."
              error={validationError ?? undefined}
              htmlFor="wiz-yaml"
            >
              <textarea
                id="wiz-yaml"
                className="textarea"
                style={{ minHeight: 320 }}
                value={yaml}
                onChange={(event) => {
                  setYaml(event.target.value);
                  setValidationError(null);
                }}
                onBlur={check}
              />
            </Field>

            <div className="row">
              <button
                type="button"
                className="button button--primary"
                disabled={busy || validationError !== null}
                onClick={commit}
              >
                Commit to the repository
              </button>
              <button type="button" className="button" onClick={check}>
                Check it
              </button>
              {validationError === null && yaml && (
                <span className="muted" style={{ fontSize: 12.5 }}>
                  Valid
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function RepoCard({ repo }: { repo: Repo }) {
  const { project, can } = useProject();
  const [syncing, setSyncing] = useState(false);
  const [result, setResult] = useState<SyncResult>();
  const [problem, setProblem] = useState<string>();
  const [onboarding, setOnboarding] = useState<UnregisteredScript>();

  const { data: unregistered, reload } = useAsync(
    () => endpoints.unregistered(project.slug, repo.id),
    [project.slug, repo.id],
  );

  const sync = async () => {
    setSyncing(true);
    setProblem(undefined);
    try {
      setResult(await endpoints.syncRepo(project.slug, repo.id));
      reload();
    } catch (caught) {
      setProblem((caught as Error).message);
    } finally {
      setSyncing(false);
    }
  };

  const pending = unregistered ?? [];

  return (
    <div className="stack">
      <div className="card">
        <div className="card__header">
          <div>
            <span className="card__title">{repo.full_name}</span>
            <div className="faint" style={{ fontSize: 12 }}>
              manifests in {repo.manifest_dir} · branch {repo.default_branch}
            </div>
          </div>
          {can("engineer") && (
            <button type="button" className="button button--small" disabled={syncing} onClick={sync}>
              {syncing ? "Syncing…" : "Sync now"}
            </button>
          )}
        </div>

        <div className="card__body stack" style={{ gap: 12 }}>
          {problem && <Notice tone="danger">{problem}</Notice>}

          {repo.last_sync_error && (
            <Notice tone="warning" title="Last sync had problems">
              {repo.last_sync_error}
            </Notice>
          )}

          {result && (
            <Notice tone="success" title="Sync complete">
              {result.scripts_created.length > 0 && (
                <div>New scripts: {result.scripts_created.join(", ")}</div>
              )}
              {result.versions_created.length > 0 && (
                <div>New versions: {result.versions_created.join(", ")}</div>
              )}
              {result.schedules_created && result.schedules_created.length > 0 && (
                <div>New schedules: {result.schedules_created.join(", ")}</div>
              )}
              {result.scripts_created.length === 0 && result.versions_created.length === 0 && (
                <div>Already up to date.</div>
              )}
            </Notice>
          )}

          <div className="faint" style={{ fontSize: 12.5 }}>
            Last synced <RelativeTime iso={repo.last_synced_at} />
          </div>
        </div>
      </div>

      {pending.length > 0 && (
        <div className="card">
          <div className="card__header">
            <span className="card__title">Not onboarded yet</span>
            <span className="muted" style={{ fontSize: 12.5 }}>
              Python files in this repository with no manifest
            </span>
          </div>
          <div className="table__scroll">
            <table className="table">
              <tbody>
                {pending.map((file) => (
                  <tr key={file.id}>
                    <td className="mono">{file.path}</td>
                    <td style={{ textAlign: "right" }}>
                      {can("engineer") && (
                        <div className="row" style={{ justifyContent: "flex-end" }}>
                          <button
                            type="button"
                            className="button button--small button--primary"
                            onClick={() => setOnboarding(file)}
                          >
                            Onboard
                          </button>
                          <button
                            type="button"
                            className="button button--small button--ghost"
                            onClick={async () => {
                              await endpoints.dismissUnregistered(project.slug, repo.id, file.id);
                              reload();
                            }}
                          >
                            Not a delivery script
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {onboarding && (
        <Wizard
          repo={repo}
          file={onboarding}
          onClose={() => setOnboarding(undefined)}
          onCommitted={() => {
            setOnboarding(undefined);
            reload();
            void sync();
          }}
        />
      )}
    </div>
  );
}

export function Repositories() {
  const { project, can } = useProject();
  const { data, error, loading, reload } = useAsync(
    () => endpoints.repos(project.slug),
    [project.slug],
  );

  if (loading) return <Loading rows={3} label="Loading script sources" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const repos = data ?? [];

  return (
    <div className="stack" style={{ gap: 20 }}>
      {can("engineer") && <UploadBundle onUploaded={reload} />}

      <div className="stack">
        <h3>Connected repositories</h3>
        {can("engineer") && <ConnectRepo onConnected={reload} />}

        {repos.length === 0 ? (
          <div className="card">
            <Empty title="No repositories connected">
              Optional. Connect a repository and scripts with a manifest appear
              automatically, with a new version on every push. Uploading a bundle above
              works just as well and needs no GitHub access.
            </Empty>
          </div>
        ) : (
          repos.map((repo) => <RepoCard key={repo.id} repo={repo} />)
        )}
      </div>
    </div>
  );
}

function UploadBundle({ onUploaded }: { onUploaded: () => void }) {
  const { project } = useProject();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string>();
  const [result, setResult] = useState<BundleUploaded>();

  // Set once the wizard has proposed a manifest for an archive that has none.
  const [draft, setDraft] = useState<BundleDraft>();
  const [yaml, setYaml] = useState("");
  const [entrypoint, setEntrypoint] = useState("");
  const [manifestError, setManifestError] = useState<string | null>(null);

  const reset = () => {
    setFile(null);
    setDraft(undefined);
    setYaml("");
    setEntrypoint("");
    setManifestError(null);
  };

  /** Upload, and fall back to the wizard when the archive has no manifest of its own. */
  const upload = async (chosen: File, manifestYaml?: string) => {
    setBusy(true);
    setProblem(undefined);
    setResult(undefined);
    try {
      const uploaded = await uploadBundle(project.slug, chosen, { manifestYaml });
      setResult(uploaded);
      reset();
      onUploaded();
    } catch (caught) {
      const message = (caught as Error).message;
      if (!manifestYaml && message.includes("No delivery manifest")) {
        await startWizard(chosen);
        return;
      }
      setProblem(message);
    } finally {
      setBusy(false);
    }
  };

  const startWizard = async (chosen: File, forEntrypoint?: string) => {
    setBusy(true);
    setProblem(undefined);
    try {
      const proposed = await draftBundleManifest(project.slug, chosen, {
        entrypoint: forEntrypoint,
      });
      setDraft(proposed);
      setYaml(proposed.yaml);
      setEntrypoint(proposed.entrypoint);
      setManifestError(null);
    } catch (caught) {
      setProblem((caught as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const check = async () => {
    const outcome = await validateBundleManifest(project.slug, yaml);
    setManifestError(outcome.valid ? null : (outcome.error ?? "Not valid."));
  };

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (file) void upload(file);
  };

  const lowConfidence = (draft?.detected ?? []).filter((p) => p.confidence === "low");

  return (
    <form className="card" onSubmit={submit}>
      <div className="card__header">
        <span className="card__title">Upload a script</span>
        <span className="muted" style={{ fontSize: 12.5 }}>
          No GitHub needed
        </span>
      </div>

      <div className="card__body stack" style={{ gap: 14 }}>
        {problem && <Notice tone="danger">{problem}</Notice>}

        {result && (
          <Notice tone="success" title={result.created ? "Uploaded" : "Already uploaded"}>
            <div>
              {result.script_name} — {result.entry_count} files, manifest at{" "}
              <span className="mono">{result.manifest_path}</span>.
            </div>
            {!result.created && (
              <div>These exact bytes were uploaded before, so no new version was made.</div>
            )}
            {result.needs_build && (
              <div style={{ marginTop: 6 }}>
                <Link to={`/projects/${project.slug}/scripts/${result.script_name}`}>
                  Build it to make it runnable
                </Link>
              </div>
            )}
          </Notice>
        )}

        {!draft ? (
          <>
            <Notice tone="info">
              A ZIP containing your script and its{" "}
              <span className="mono">requirements.txt</span>. If it has a delivery manifest
              at the root or in a <span className="mono">.delivery</span> directory, that is
              used; if not, the wizard reads your script and writes one for you.
            </Notice>

            <Field label="Archive" required htmlFor="bundle-file">
              <input
                id="bundle-file"
                className="input"
                type="file"
                accept=".zip,application/zip"
                disabled={busy}
                onChange={(event) => {
                  setFile(event.target.files?.[0] ?? null);
                  setResult(undefined);
                  setProblem(undefined);
                }}
              />
            </Field>

            <button type="submit" className="button button--primary" disabled={busy || !file}>
              {busy ? "Reading the archive…" : "Upload"}
            </button>
          </>
        ) : (
          <>
            <Notice tone={draft.framework ? "success" : "warning"}>
              {draft.framework
                ? `This archive has no manifest, so one was written for you from the ${draft.framework} interface in ${draft.entrypoint} — ${draft.detected.length} parameters.`
                : `This archive has no manifest, and no argparse or click interface was found in ${draft.entrypoint}. Add parameters by hand below.`}
            </Notice>

            {draft.warnings.length > 0 && (
              <Notice tone="warning" title="Worth checking">
                {draft.warnings.map((warning, index) => (
                  <div key={index}>{warning}</div>
                ))}
              </Notice>
            )}

            {lowConfidence.length > 0 && (
              <Notice tone="warning" title="Check these before uploading">
                These could not be read literally, so they were guessed:{" "}
                {lowConfidence.map((p) => p.name).join(", ")}.
              </Notice>
            )}

            {draft.candidates.length > 1 && (
              <Field
                label="Entrypoint"
                hint="Which file the platform should run. Changing it rewrites the draft."
                htmlFor="bundle-entrypoint"
              >
                <select
                  id="bundle-entrypoint"
                  className="select mono"
                  value={entrypoint}
                  disabled={busy}
                  onChange={(event) => {
                    setEntrypoint(event.target.value);
                    if (file) void startWizard(file, event.target.value);
                  }}
                >
                  {draft.candidates.map((candidate) => (
                    <option key={candidate} value={candidate}>
                      {candidate}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <Field
              label="Manifest"
              hint="Stored with this version. Your ZIP is not modified."
              error={manifestError ?? undefined}
              htmlFor="bundle-manifest-yaml"
            >
              <textarea
                id="bundle-manifest-yaml"
                className="textarea"
                style={{ minHeight: 320 }}
                value={yaml}
                disabled={busy}
                onChange={(event) => {
                  setYaml(event.target.value);
                  setManifestError(null);
                }}
                onBlur={check}
              />
            </Field>

            <div className="row">
              <button
                type="button"
                className="button button--primary"
                disabled={busy || manifestError !== null || !file}
                onClick={() => file && void upload(file, yaml)}
              >
                {busy ? "Uploading…" : "Upload with this manifest"}
              </button>
              <button type="button" className="button" disabled={busy} onClick={check}>
                Check it
              </button>
              <button type="button" className="button button--ghost" onClick={reset}>
                Start over
              </button>
            </div>
          </>
        )}
      </div>
    </form>
  );
}
